import { useCallback, useEffect, useRef } from "react";

interface UseFieldTouchOptions {
  onTouch: (path: string) => void;
  path: string;
  disabled?: boolean;
}

export const useFieldTouch = ({ onTouch, path, disabled = false }: UseFieldTouchOptions) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasBeenTouchedRef = useRef(false);

  const isFormElement = (element: Element): boolean => {
    const tagName = element.tagName.toLowerCase();
    return ["input", "select", "textarea"].includes(tagName);
  };

  const isInteractiveElement = (element: Element): boolean => {
    const tagName = element.tagName.toLowerCase();
    const type = (element as HTMLInputElement).type?.toLowerCase();

    return (
      tagName === "button" ||
      (tagName === "input" && ["checkbox", "radio", "submit", "button"].includes(type)) ||
      element.getAttribute("role") === "button" ||
      element.classList.contains("cursor-pointer")
    );
  };

  const markAsTouched = useCallback(() => {
    if (!disabled && !hasBeenTouchedRef.current && path) {
      hasBeenTouchedRef.current = true;
      onTouch(path);
    }
  }, [onTouch, path, disabled]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || disabled) {
      return;
    }

    const handleInput = (event: Event) => {
      const target = event.target as Element;
      if (isFormElement(target)) {
        markAsTouched();
      }
    };

    const handleChange = (event: Event) => {
      const target = event.target as Element;
      if (isFormElement(target) || isInteractiveElement(target)) {
        markAsTouched();
      }
    };

    // Use capture phase to ensure we catch events before they bubble
    container.addEventListener("input", handleInput, { capture: true });
    container.addEventListener("change", handleChange, { capture: true });

    return () => {
      container.removeEventListener("input", handleInput, { capture: true });
      container.removeEventListener("change", handleChange, { capture: true });
    };
  }, [markAsTouched, disabled]);

  // Reset touched state when path changes
  useEffect(() => {
    hasBeenTouchedRef.current = false;
  }, [path]);

  return {
    containerRef,
    markAsTouched,
  };
};
